import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { type PromptHistoryFileType, type PromptHistoryRow, useHydrateFromHistory, useLatestPromptHistory } from "../../hooks";
import type { DataLoadFile } from "./consts";
import { applySeedToFiles, type PromptSeeds } from "./dataLoadPrompts";

type Params = {
	departmentId: string | undefined;
	/** Owned by the composition root, because `useDataLoadFiles` reads it on every add and kind change. */
	seedsRef: MutableRefObject<PromptSeeds>;
	setFiles: Dispatch<SetStateAction<DataLoadFile[]>>;
	/**
	 * Seeding is frozen while a run is in flight. `chosenDepartment` is switchable from the header at
	 * any time, and a back-fill firing mid-run would rewrite the prompts of files still queued behind
	 * the one currently loading. A ref because the freeze has to be read at callback time, not at the
	 * render that registered it.
	 */
	frozenRef: MutableRefObject<boolean>;
};

/**
 * Seeds each file's prompt from the latest prompt-history row for its kind.
 *
 * The backend keys history only by (department, file type), so every file of a kind seeds from the
 * same row. The seed is a starting point, not an identity — `promptDirty` is what protects anything
 * the onboarder types from here on.
 */
export function useDataLoadPrompts({ departmentId, seedsRef, setFiles, frozenRef }: Params) {
	const nurses = useLatestPromptHistory("nurses");
	const shifts = useLatestPromptHistory("shifts");
	const template = useLatestPromptHistory("template");

	const byType: Record<PromptHistoryFileType, { data: PromptHistoryRow | null | undefined; isLoading: boolean }> = {
		nurses: { data: nurses.data, isLoading: nurses.isLoading },
		shifts: { data: shifts.data, isLoading: shifts.isLoading },
		template: { data: template.data, isLoading: template.isLoading },
	};

	const record = (fileType: PromptHistoryFileType, prompt: string, tabInstructions: Record<string, string>) => {
		seedsRef.current = { ...seedsRef.current, [fileType]: { prompt, tabInstructions } };
		if (frozenRef.current) return;
		setFiles((prev) => applySeedToFiles(prev, fileType, seedsRef.current[fileType]));
	};

	// One hydration per (department, file type). It fires once per key, so a file dropped AFTER it has
	// run is seeded by `addFiles` reading `seedsRef` instead — both write only to non-dirty files.
	useHydrateFromHistory({
		key: `${departmentId}:nurses`,
		isLoading: byType.nurses.isLoading,
		serverData: byType.nurses.data,
		onHydrate: (row) => record("nurses", row.prompt, row.tab_instructions),
		onFallback: () => record("nurses", "", {}),
	});
	useHydrateFromHistory({
		key: `${departmentId}:shifts`,
		isLoading: byType.shifts.isLoading,
		serverData: byType.shifts.data,
		onHydrate: (row) => record("shifts", row.prompt, row.tab_instructions),
		onFallback: () => record("shifts", "", {}),
	});
	useHydrateFromHistory({
		key: `${departmentId}:template`,
		isLoading: byType.template.isLoading,
		serverData: byType.template.data,
		onHydrate: (row) => record("template", row.prompt, row.tab_instructions),
		onFallback: () => record("template", "", {}),
	});

	return { isLoadingHistory: nurses.isLoading || shifts.isLoading || template.isLoading };
}
